/* DA de la base : comportements communs (cartes produit, favoris, carrousels, compte à rebours). */
(() => {
  'use strict';

  const el = (tag, attrs = {}, enfants = []) => {
    const n = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => {
      if (k === 'texte') n.textContent = v;
      else if (v !== false && v != null) n.setAttribute(k, v === true ? '' : v);
    });
    enfants.forEach((e) => e && n.appendChild(e));
    return n;
  };

  /* ---------- Carrousel horizontal (flèches + tirets) ---------- */
  class DaCarrousel extends HTMLElement {
    connectedCallback() {
      this.piste = this.querySelector('[data-piste]');
      if (!this.piste) return;
      this.prec = this.querySelector('[data-precedent]');
      this.suiv = this.querySelector('[data-suivant]');
      this.points = this.querySelector('[data-points]');
      const pas = () => {
        const premier = this.piste.firstElementChild;
        const ecart = parseFloat(getComputedStyle(this.piste).columnGap) || 0;
        return premier ? premier.getBoundingClientRect().width + ecart : this.piste.clientWidth;
      };
      const boucle = this.hasAttribute('boucle');
      const fin = () => this.piste.scrollWidth - this.piste.clientWidth - 2;
      if (this.prec)
        this.prec.addEventListener('click', () => {
          if (boucle && this.piste.scrollLeft <= 2) this.piste.scrollTo({ left: this.piste.scrollWidth, behavior: 'smooth' });
          else this.piste.scrollBy({ left: -pas(), behavior: 'smooth' });
        });
      if (this.suiv)
        this.suiv.addEventListener('click', () => {
          if (boucle && this.piste.scrollLeft >= fin()) this.piste.scrollTo({ left: 0, behavior: 'smooth' });
          else this.piste.scrollBy({ left: pas(), behavior: 'smooth' });
        });
      const maj = () => {
        const max = fin();
        // Rien à faire défiler (moins de cartes que de places) : pas de flèches, même en boucle.
        if (this.prec) this.prec.hidden = max <= 0 || (!boucle && this.piste.scrollLeft <= 2);
        if (this.suiv) this.suiv.hidden = max <= 0 || (!boucle && this.piste.scrollLeft >= max);
        if (!this.points) return;
        const pages = Math.max(1, Math.ceil(this.piste.scrollWidth / Math.max(1, this.piste.clientWidth) - 0.01));
        if (this.points.children.length !== pages) {
          this.points.replaceChildren();
          for (let i = 0; i < pages; i++) {
            const b = el('button', { type: 'button', 'aria-label': `Page ${i + 1}` });
            b.addEventListener('click', () => this.piste.scrollTo({ left: i * this.piste.clientWidth, behavior: 'smooth' }));
            this.points.appendChild(b);
          }
        }
        const page = max <= 0 ? 0 : Math.round((this.piste.scrollLeft / max) * (pages - 1));
        [...this.points.children].forEach((b, i) => b.toggleAttribute('actif', i === page));
        this.points.hidden = pages < 2;
      };
      // Mesures seulement au défilement (une par image) et dans le ResizeObserver, qui s'appelle
      // aussi une première fois après la mise en page : un maj() immédiat forçait un recalcul
      // pendant la construction de la page (Lighthouse, 25/09).
      let prevu = false;
      this.piste.addEventListener('scroll', () => {
        if (prevu) return;
        prevu = true;
        requestAnimationFrame(() => { prevu = false; maj(); });
      }, { passive: true });
      new ResizeObserver(maj).observe(this.piste);
    }
  }

  /* ---------- Onglets ---------- */
  class DaOnglets extends HTMLElement {
    connectedCallback() {
      const boutons = [...this.querySelectorAll('[role="tab"]')];
      const panneaux = [...this.querySelectorAll('[role="tabpanel"]')];
      boutons.forEach((b, i) =>
        b.addEventListener('click', () => {
          boutons.forEach((x, j) => x.setAttribute('aria-selected', String(i === j)));
          panneaux.forEach((p, j) => (p.hidden = i !== j));
        })
      );
    }
  }

  /* ---------- Texte replié (« Voir plus ») ---------- */
  class DaRepli extends HTMLElement {
    connectedCallback() {
      const contenu = this.querySelector('[data-contenu]');
      const bouton = this.querySelector('[data-basculer]');
      if (!contenu || !bouton) return;
      requestAnimationFrame(() => {
        if (contenu.scrollHeight <= contenu.clientHeight + 4) this.setAttribute('court', '');
        else bouton.hidden = false;
      });
      bouton.addEventListener('click', () => {
        const ouvert = this.toggleAttribute('ouvert');
        bouton.textContent = ouvert ? bouton.dataset.moins : bouton.dataset.plus;
      });
    }
  }


  /* ---------- Fiche produit : paliers de quantité ---------- */
  const formulaireProduit = (depuis) => {
    const racine = depuis.closest('.shopify-section, product-component') || document;
    return racine.querySelector('form[data-type="add-to-cart-form"][id^="BuyButtons"]') || document.querySelector('form[id^="BuyButtons-ProductForm"]');
  };

  // Montant au format monétaire de la boutique (shop.money_format), décimales nulles masquées
  const formatPrix = (centimes, format) => {
    const f = String(format || (window.daFormatArgent) || '€').replace(/<[^>]*>/g, '');
    const m = f.match(/\{\{\s*(\w+)\s*\}\}/);
    const nombre = (dec, milliers, virgule) => {
      const [ent, frac] = (centimes / 100).toFixed(dec).split('.');
      const e = ent.replace(/\B(?=(\d{3})+(?!\d))/g, milliers);
      return frac ? e + virgule + frac : e;
    };
    const types = {
      amount: () => nombre(2, ',', '.'),
      amount_no_decimals: () => nombre(0, ',', '.'),
      amount_with_comma_separator: () => nombre(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => nombre(0, '.', ','),
      amount_with_apostrophe_separator: () => nombre(2, "'", '.'),
      amount_with_space_separator: () => nombre(2, ' ', ','),
    };
    let valeur = (types[m ? m[1] : 'amount'] || types.amount)();
    if (window.daSansZeros !== false) valeur = valeur.replace(/[.,]00$/, '');
    return m ? f.replace(m[0], valeur) : valeur;
  };

  // N'écrit que si le texte change (évite de relancer l'observateur)
  const ecrireTexte = (n, t) => {
    if (n && n.textContent !== t) n.textContent = t;
  };

  class DaPaliers extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('.da-palier').forEach((b) => b.addEventListener('click', () => this.choisir(b)));
      const actif = this.querySelector('.da-palier[aria-checked="true"]');
      if (actif) requestAnimationFrame(() => this.choisir(actif, true));
      try {
        const d = JSON.parse(this.querySelector('[data-prix]').textContent);
        this.format = d.format;
        this.prix = d.v || d;
      } catch (e) {
        this.prix = null;
      }
      // Horizon remplace le prix du haut quand la variante change : on recalcule après chaque remplacement
      this.section = this.closest('.shopify-section') || document.body;
      let attente = 0;
      this.planifier = () => {
        cancelAnimationFrame(attente);
        attente = requestAnimationFrame(() => this.majPrix());
      };
      this.section.addEventListener('change', this.planifier);
      this.observateur = new MutationObserver(this.planifier);
      this.observateur.observe(this.section, { childList: true, subtree: true, characterData: true });
      this.planifier();
    }
    disconnectedCallback() {
      if (this.observateur) this.observateur.disconnect();
      if (this.section) this.section.removeEventListener('change', this.planifier);
    }
    majPrix() {
      const form = formulaireProduit(this);
      const champ = form && form.querySelector('input[name="id"]');
      if (!champ || !this.prix || !this.prix[champ.value]) return;
      const [prix, compare] = this.prix[champ.value];
      const barreUnite = Math.max(prix, compare);
      const calcul = (b) => {
        const q = Number(b.dataset.quantite) || 1;
        return { total: Math.floor((prix * q * (100 - (Number(b.dataset.remise) || 0))) / 100), barre: barreUnite * q };
      };
      // cartes des paliers
      this.querySelectorAll('.da-palier').forEach((b) => {
        const cible = b.querySelector('[data-prix-palier]');
        if (!cible) return;
        const { total, barre } = calcul(b);
        const [span, s] = [cible.querySelector('span'), cible.querySelector('s')];
        if (!span) return;
        ecrireTexte(span, formatPrix(total, this.format));
        if (barre > total) {
          if (s) ecrireTexte(s, formatPrix(barre, this.format));
          else cible.appendChild(el('s', { texte: formatPrix(barre, this.format) }));
        } else if (s) s.remove();
      });
      // prix du haut : total du palier choisi, prix barré et économie
      const actif = this.querySelector('.da-palier[aria-checked="true"]');
      if (!actif) return;
      const { total, barre } = calcul(actif);
      this.section.querySelectorAll('.da-prix-produit').forEach((pp) => {
        const ligne = pp.querySelector('.da-prix-produit__ligne');
        const prixHaut = pp.querySelector('.da-prix-produit__prix');
        if (!ligne || !prixHaut) return;
        ecrireTexte(prixHaut, formatPrix(total, this.format));
        let s = pp.querySelector('.da-prix-produit__barre');
        let badge = pp.querySelector('.da-prix-produit__ligne > .da-badge--economie');
        if (barre > total) {
          const economie = Math.round(((barre - total) * 100) / barre);
          if (!s) {
            s = el('s', { class: 'da-prix-produit__barre' });
            ligne.appendChild(s);
          }
          if (!badge) {
            badge = el('span', { class: 'da-badge da-badge--economie' });
            ligne.appendChild(badge);
          }
          ecrireTexte(s, formatPrix(barre, this.format));
          ecrireTexte(badge, `${pp.dataset.libelle || 'Économisez'} ${economie}%`);
        } else {
          if (s) s.remove();
          if (badge) badge.remove();
        }
      });
    }
    choisir(bouton, silencieux) {
      this.querySelectorAll('.da-palier').forEach((b) => b.setAttribute('aria-checked', String(b === bouton)));
      if (this.planifier) this.planifier();
      const form = formulaireProduit(this);
      if (!form) return;
      let champ = form.querySelector('input[name="quantity"]');
      if (!champ) {
        champ = el('input', { type: 'hidden', name: 'quantity' });
        form.appendChild(champ);
      }
      champ.value = bouton.dataset.quantite;
      if (!silencieux) champ.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* ---------- Fiche produit : options ajoutées avec le produit ---------- */
  let ajoutOptionsEnCours = false;
  document.addEventListener(
    'click',
    async (e) => {
      const bouton = e.target.closest('button[name="add"]');
      if (!bouton || ajoutOptionsEnCours) return;
      const form = bouton.closest('form');
      if (!form || !(form.getAttribute('id') || '').startsWith('BuyButtons')) return;
      const racine = form.closest('.shopify-section') || document;
      const cochees = [...racine.querySelectorAll('da-options .da-case:checked')];
      if (!cochees.length) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const racineUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      try {
        await fetch(`${racineUrl}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: cochees.map((c) => ({ id: Number(c.dataset.variante), quantity: 1 })) }),
        });
      } catch (err) {
        /* l'ajout du produit principal continue */
      }
      ajoutOptionsEnCours = true;
      bouton.click();
      ajoutOptionsEnCours = false;
    },
    true
  );

  /* ---------- Accessoires proposés : la variante choisie suit le sélecteur ----------
     Fiche (« Complétez votre achat », « On vous recommande aussi ») et tiroir du panier : la case
     ajoute la couleur choisie, et la vignette (et sa version agrandie) montre cette couleur. */
  const photoDeLaCouleur = (bloc, option) => {
    if (!bloc || !option) return;
    const bouton = bloc.querySelector('.da-zoom-declencheur');
    const image = bloc.querySelector('.da-entretien__image img, .da-mini-carte__image img, .da-option__image img, .da-accessoire__image img');
    if (image && option.dataset.image) {
      image.removeAttribute('srcset');
      image.src = option.dataset.image;
    }
    if (bouton && option.dataset.zoom) bouton.dataset.zoom = option.dataset.zoom;
  };
  document.addEventListener('change', (e) => {
    const choix = e.target.closest('[data-choix-variante]');
    if (choix) {
      const ligne = choix.closest('.da-option, .da-entretien__ligne');
      const option = choix.selectedOptions[0];
      const caseOption = ligne?.querySelector('.da-case');
      if (caseOption) {
        caseOption.dataset.variante = choix.value;
        if (option?.dataset.prix) caseOption.dataset.prix = option.dataset.prix;
      }
      const prix = ligne?.querySelector('[data-prix-affiche]');
      // offre de lancement −20 % : le prix affiché a deux textes (habituel / remisé), la classe da-promo choisit
      const [sans, avec] = prix ? [prix.querySelector('.da-promo-sans'), prix.querySelector('.da-promo-avec')] : [];
      if (sans && avec && option?.dataset.prixTextePromo) {
        sans.textContent = option.dataset.prixTexte;
        avec.textContent = option.dataset.prixTextePromo;
      } else if (prix && option?.dataset.prixTexte) prix.textContent = option.dataset.prixTexte;
      photoDeLaCouleur(ligne, option);
      return;
    }
    // Mini-cartes et accessoires du volet Entretien : la vignette suit la couleur choisie.
    const liste = e.target.closest('.da-mini-carte select[name="id"], .da-accessoire select[name="id"]');
    if (liste) photoDeLaCouleur(liste.closest('.da-mini-carte, .da-accessoire'), liste.selectedOptions[0]);
  });

  /* ---------- Vignettes d'accessoires : agrandir la photo sans quitter la page ----------
     Fenêtre légère au-dessus de tout (y compris du tiroir du panier), fermée par la croix,
     un clic à côté ou Échap. */
  let fenetreZoom = null;
  const ouvrirZoom = (declencheur) => {
    if (!fenetreZoom) {
      fenetreZoom = el('dialog', { class: 'da-zoom', 'aria-label': 'Photo agrandie' });
      const fermer = el('button', { type: 'button', class: 'da-zoom__fermer', 'aria-label': 'Fermer' });
      fermer.textContent = '×';
      fermer.addEventListener('click', () => fenetreZoom.close());
      fenetreZoom.addEventListener('click', (e) => {
        if (e.target === fenetreZoom) fenetreZoom.close();
      });
      fenetreZoom.append(el('img', { class: 'da-zoom__image', alt: '' }), fermer);
      document.body.appendChild(fenetreZoom);
    }
    const image = fenetreZoom.querySelector('img');
    image.src = declencheur.dataset.zoom;
    image.alt = declencheur.dataset.zoomAlt || '';
    if (!fenetreZoom.open) fenetreZoom.showModal();
  };
  document.addEventListener('click', (e) => {
    const declencheur = e.target.closest('.da-zoom-declencheur');
    if (!declencheur || !declencheur.dataset.zoom) return;
    e.preventDefault();
    e.stopPropagation();
    ouvrirZoom(declencheur);
  });

  /* ---------- Date de livraison estimée ---------- */
  class DaEstimation extends HTMLElement {
    connectedCallback() {
      const ajouter = (jours) => {
        const d = new Date();
        let reste = Number(jours) || 0;
        while (reste > 0) {
          d.setDate(d.getDate() + 1);
          if (d.getDay() !== 0 && d.getDay() !== 6) reste--;
        }
        if (this.dataset.format === 'long') return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
      };
      const debut = this.querySelector('[data-debut]');
      const fin = this.querySelector('[data-fin]');
      if (debut) debut.textContent = ajouter(this.dataset.min);
      if (fin) fin.textContent = ajouter(this.dataset.max);
    }
  }

  const definir = (nom, classe) => customElements.get(nom) || customElements.define(nom, classe);
  definir('da-carrousel', DaCarrousel);
  definir('da-onglets', DaOnglets);
  definir('da-repli', DaRepli);
  definir('da-paliers', DaPaliers);
  definir('da-estimation', DaEstimation);

  /* ---------- Fiche produit : barre d'achat collante en bas de l'écran (démo) ----------
     Ordinateur : visible une fois le bouton d'ajout dépassé ; mobile : dès que le bouton
     n'est pas à l'écran. Masquée quand le pied de page est visible. */
  const barreCollante = () => {
    const boutons = document.querySelector('.product-details .product-form-buttons');
    if (!boutons || document.querySelector('.da-barre-achat')) return;
    const vrai = boutons.querySelector('.add-to-cart-button, button[name="add"]');
    if (!vrai) return;
    const barre = el('div', { class: 'da-barre-achat', 'aria-hidden': 'true' });
    const bouton = el('button', { type: 'button', class: 'da-barre-achat__bouton', tabindex: '-1' });
    const copier = () => {
      // 10/10 : « Ajouter au panier | 169,90 € » comme le bouton de la fiche (le trait vient du CSS) ;
      // pendant l'ajout, le même « Ajout en cours… » que le bouton (voir ajoutEnCours)
      // 12/10 : pendant l'offre de lancement, le prix du bouton contient l'ancien prix barré et le nouveau ; on
      // déplace ces éléments tels quels (avant, le texte des deux prix collés donnait « 143,92 €179,90 € »).
      const copie = vrai.cloneNode(true);
      copie.querySelectorAll('.da-chargement').forEach((c) => c.remove());
      const prix = copie.querySelector('.da-prix-bouton');
      if (prix) prix.remove();
      const tout = (copie.querySelector('.add-to-cart-text__content, .add-to-cart-text') || copie).textContent;
      const texte = tout.replace(/\s+/g, ' ').trim();
      const span = el('span', { class: 'da-barre-achat__prix' });
      if (prix) span.append(...prix.childNodes);
      const enCours = vrai.classList.contains('da-ajout-en-cours');
      bouton.replaceChildren(document.createTextNode(texte), span, ...(enCours ? [chargement()] : []));
      bouton.classList.toggle('da-ajout-en-cours', enCours);
      bouton.disabled = vrai.disabled;
    };
    copier();
    new MutationObserver(copier).observe(vrai, { subtree: true, childList: true, characterData: true, attributes: true });
    bouton.addEventListener('click', () => vrai.click());
    barre.appendChild(bouton);
    document.body.appendChild(barre);
    const mobile = window.matchMedia('(max-width: 749px)');
    let visibles = true;
    let pied = false;
    const maj = () => {
      const r = boutons.getBoundingClientRect();
      const montrer = !visibles && !pied && (mobile.matches || r.bottom < 0);
      barre.classList.toggle('da-barre-achat--visible', montrer);
      barre.setAttribute('aria-hidden', String(!montrer));
    };
    new IntersectionObserver(([e]) => { visibles = e.isIntersecting; maj(); }).observe(boutons);
    const footer = document.querySelector('.shopify-section-group-footer-group:last-of-type, footer');
    if (footer) new IntersectionObserver(([e]) => { pied = e.isIntersecting; maj(); }).observe(footer);
    mobile.addEventListener('change', maj);
    // le passage d'un côté à l'autre du bouton sans le croiser ne déclenche pas l'observateur
    let attente = 0;
    const surDefilement = () => {
      cancelAnimationFrame(attente);
      attente = requestAnimationFrame(maj);
    };
    window.addEventListener('scroll', surDefilement, { passive: true });
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper) wrapper.addEventListener('scroll', surDefilement, { passive: true });
  };
  /* ---------- Fiche produit : la pointure se choisit (08/10) ----------
     Horizon coche d'office la première pointure disponible (34, 35…) : un clic sur « Ajouter au panier », ou sur la
     barre collante qui clique ce bouton, ajoutait cette pointure sans que le visiteur l'ait choisie. Tant qu'il n'a
     touché aucune pointure, aucune n'apparaît choisie (classe sur <body> : Horizon redessine le sélecteur à chaque
     changement de coloris) et l'ajout au panier remonte aux pointures avec « Choisissez votre pointure ». */
  const POINTURE = 'input[type="radio"][data-option-name="Pointure"], input[type="radio"][data-option-name="Taille"]';
  const groupePointure = () => {
    const r = [...document.querySelectorAll(POINTURE)].find((i) => i.closest('.product-details'));
    return r ? r.closest('fieldset') : null;
  };
  const pointureAChoisir = () => {
    const fs = groupePointure();
    if (!fs || fs.querySelectorAll('input[type="radio"]').length < 2) return;
    document.body.classList.add('da-pointure-a-choisir');
    let choisie = false;
    const choisir = (cible) => {
      if (choisie || !cible?.closest?.('.product-details')) return;
      const radio = cible.matches?.(POINTURE) ? cible : cible.closest?.('label')?.querySelector(POINTURE);
      if (!radio) return;
      choisie = true;
      document.body.classList.remove('da-pointure-a-choisir');
      document.querySelectorAll('.da-pointure-message').forEach((m) => m.remove());
    };
    // « change » ne part pas quand on clique la pointure déjà cochée : on écoute aussi le clic
    document.addEventListener('change', (e) => choisir(e.target), true);
    document.addEventListener('click', (e) => choisir(e.target), true);
    const signaler = () => {
      const g = groupePointure();
      if (!g) return;
      g.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const legende = g.querySelector('legend');
      if (legende && !legende.querySelector('.da-pointure-message')) {
        const m = el('span', { class: 'da-pointure-message', role: 'alert' });
        m.textContent = 'Choisissez votre pointure';
        legende.appendChild(m);
      }
      g.classList.remove('da-pointure-secoue');
      void g.offsetWidth;
      g.classList.add('da-pointure-secoue');
    };
    const estAjout = (cible) => cible?.closest?.('.product-details .product-form-buttons') && cible.closest('button[name="add"], .add-to-cart-button');
    document.addEventListener('click', (e) => {
      if (choisie || !estAjout(e.target)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      signaler();
    }, true);
    document.addEventListener('submit', (e) => {
      const f = e.target;
      if (choisie || !f.closest?.('.product-details') || !f.querySelector('.product-form-buttons')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      signaler();
    }, true);
  };

  /* ---------- Fiche produit : encadré « Quelle pointure choisir ? » (08/10) ----------
     Remonte aux pointures, puis ouvre le guide des pointures du bloc d'achat : en le fermant, on choisit. */
  document.addEventListener('click', (e) => {
    const b = e.target.closest?.('[data-da-vers-pointure]');
    if (!b) return;
    e.preventDefault();
    const ouvrir = () => (document.querySelector('.product-details [data-ancre="guide-pointures"]') || document.querySelector('[data-ancre="guide-pointures"]'))?.click();
    const g = groupePointure();
    if (!g) { ouvrir(); return; }
    g.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(ouvrir, 550);
  });

  /* ---------- Conseiller « Trouver ma pointure » (08/10, refait façon Zara) ----------
     Étape 1 : longueur du pied (− / + par demi-centimètre). Étape 2 : pied fin, normal ou large. Résultat : la
     pointure du tableau de CE modèle égale ou juste au-dessus de la mesure, corrigée d'après les remarques de taille
     de ses acheteurs (data-ajustement / data-avis, snippet da-ajustement-pointure) et la largeur du pied, avec
     l'explication ; « Choisir le 39 » la coche sur la fiche. Pas de chemin « pointure habituelle » (retiré le 08/10 : on mesure).
     Tout se calcule ici, sans requête : le tableau est lu dans la fenêtre (snippet da-guide-pointures). */
  const virgule = (n) => String(Math.round(n * 10) / 10).replace('.', ',');
  const tableauPointures = (bloc) => {
    const table = (bloc.closest('dialog') || document).querySelector('.da-guide__tableau table');
    if (!table) return [];
    return [...table.querySelectorAll('tbody tr')].map((tr) => {
      const p = parseFloat((tr.querySelector('th') || tr.cells[0])?.textContent.replace(',', '.'));
      const cm = parseFloat((tr.querySelector('td') || tr.cells[1])?.textContent.replace(',', '.'));
      return { p, cm };
    }).filter((r) => r.p > 0 && r.cm > 0).sort((a, b) => a.cm - b.cm || a.p - b.p);
  };
  const etatConseiller = new WeakMap();
  const montrerEtape = (bloc, nom) => {
    bloc.querySelectorAll('[data-da-etape]').forEach((e) => { e.hidden = e.dataset.daEtape !== nom; });
  };
  const afficherLongueur = (bloc) => {
    const etat = etatConseiller.get(bloc);
    bloc.querySelector('[data-da-longueur]').textContent = `${virgule(etat.longueur)} cm`;
  };
  const preparerConseiller = (bloc) => {
    if (etatConseiller.has(bloc)) return etatConseiller.get(bloc);
    const tableau = tableauPointures(bloc);
    const milieu = tableau.length ? tableau[Math.floor((tableau.length - 1) / 2)].cm : 24;
    const etat = {
      tableau,
      longueur: milieu,
      mini: tableau.length ? tableau[0].cm : 20,
      maxi: tableau.length ? tableau[tableau.length - 1].cm : 31,
    };
    etatConseiller.set(bloc, etat);
    afficherLongueur(bloc);
    return etat;
  };
  const conseillerPointure = (bloc, { longueur, largeur = 'normal' }) => {
    const etat = preparerConseiller(bloc);
    const proposees = (bloc.dataset.pointures || '').split(',').map(Number).filter((n) => n > 0).sort((a, b) => a - b);
    const aj = bloc.dataset.ajustement || '';
    const lignes = [];
    // La mesure est bornée au tableau du modèle (− / +) : il y a toujours une pointure qui couvre le pied
    const ligne = etat.tableau.find((r) => r.cm >= longueur - 0.001) || etat.tableau[etat.tableau.length - 1];
    const base = ligne.p;
    const marge = ligne.cm - longueur;
    lignes.push(`Pied de ${virgule(longueur)} cm : c'est le ${ligne.p} dans le tableau de ce modèle.`);
    // Phrases courtes, seulement quand elles changent quelque chose pour le client (retour de l'utilisateur, 08/10)
    let ecart = 0;
    const juste = marge < 0.25;  // pied pile à la limite de la pointure du tableau
    const large = largeur === 'large' && juste;
    if (aj === 'petit') {
      ecart = 1;
      lignes.push('Ce modèle taille petit selon ses acheteurs : on vous conseille une pointure au-dessus.');
    } else if (aj === 'grand' && large) {
      lignes.push('Ce modèle taille grand, mais votre pied large compense : gardez cette pointure.');
    } else if (aj === 'grand') {
      ecart = -1;
      lignes.push('Ce modèle taille grand selon ses acheteurs : on vous conseille une pointure en dessous.');
    } else if (aj === 'petit-leger' && juste) {
      ecart = 1;
      lignes.push('Ce modèle taille un peu petit et vous êtes pile à la limite : prenez une pointure au-dessus.');
    } else if (large) {
      ecart = 1;
      lignes.push('Pied large : la pointure au-dessus vous laissera plus de place.');
    }
    let conseil = base + ecart;
    if (!proposees.includes(conseil)) conseil = base;  // la pointure corrigée n'existe pas : on reste sur celle du tableau
    if (!proposees.includes(conseil)) return { lignes: ["Cette pointure n'existe pas dans ce modèle."] };
    const radio = [...document.querySelectorAll(POINTURE)].find((i) => i.closest('.product-details') && Number(i.value) === conseil);
    if (radio && radio.dataset.optionAvailable === 'false') lignes.push(`Le ${conseil} est épuisé en ce moment dans ce coloris.`);
    return { conseil, lignes, radio };
  };
  const afficherResultat = (bloc, res) => {
    const zone = bloc.querySelector('[data-da-pointure-conseillee]');
    const liste = bloc.querySelector('[data-da-explications]');
    const choisir = bloc.querySelector('[data-da-conseiller-choisir]');
    zone.textContent = res.conseil ? String(res.conseil) : '';
    zone.hidden = !res.conseil;
    const textes = [...(res.lignes || []), ...(res.message ? [res.message] : [])];
    liste.replaceChildren(...textes.map((t) => { const li = document.createElement('li'); li.textContent = t; return li; }));
    choisir.hidden = !res.conseil || !res.radio;
    if (res.conseil) {
      choisir.textContent = `Choisir le ${res.conseil}`;
      choisir.dataset.pointure = String(res.conseil);
    }
    montrerEtape(bloc, 'resultat');
  };
  document.addEventListener('click', (e) => {
    const cible = e.target.closest?.('[data-da-conseiller] button');
    if (!cible) return;
    const bloc = cible.closest('[data-da-conseiller]');
    const etat = preparerConseiller(bloc);
    if (cible.dataset.daMesure) {
      etat.longueur = Math.min(etat.maxi, Math.max(etat.mini, etat.longueur + Number(cible.dataset.daMesure)));
      afficherLongueur(bloc);
    } else if (cible.dataset.daAller) {
      montrerEtape(bloc, cible.dataset.daAller);
    } else if (cible.dataset.daLargeur) {
      bloc.querySelectorAll('[data-da-largeur]').forEach((b) => b.toggleAttribute('data-actif', b === cible));
      afficherResultat(bloc, conseillerPointure(bloc, { longueur: etat.longueur, largeur: cible.dataset.daLargeur }));
    } else if (cible.hasAttribute('data-da-conseiller-choisir')) {
      const radio = [...document.querySelectorAll(POINTURE)].find((i) => i.closest('.product-details') && i.value === cible.dataset.pointure);
      cible.closest('dialog')?.close();
      if (radio) {
        (radio.closest('label') || radio).click();
        radio.closest('fieldset')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  });
  // À l'ouverture de la fenêtre, la mesure affichée part du milieu du tableau du modèle
  document.addEventListener('click', (e) => {
    if (!e.target.closest?.('.da-pointure-pastille, [data-da-vers-pointure]')) return;
    setTimeout(() => document.querySelectorAll('dialog[open] [data-da-conseiller]').forEach(preparerConseiller), 700);
  });

  /* ---------- Bouton d'ajout : retour immédiat au clic (10/10) ----------
     Entre le clic et l'ouverture du panier il y a 0,4 à 1 s (serveur Shopify ~0,3-0,4 s, puis dessin du tiroir) et le
     bouton ne bougeait pas (l'animation « ajouté » d'Horizon est coupée dans css/99d) : on affiche tout de suite
     « Ajout en cours… » avec un petit rond qui tourne, sur le bouton et sur la barre collée, jusqu'à l'ouverture du
     panier (6 s au plus, en cas d'erreur). Rien quand la pointure reste à choisir (pointureAChoisir remonte alors
     aux pointures) ni quand le bouton est désactivé. Les accessoires sous le bouton ont leur propre retour (bloc
     suivant) : leur ajout n'ouvre pas le panier, et ce texte blanc ne se voyait pas sur leur bouton clair. */
  function chargement() {
    return el('span', { class: 'da-chargement', 'aria-hidden': 'true' }, [el('span', { class: 'da-chargement__rond' }), document.createTextNode('Ajout en cours…')]);
  }
  const ajoutEnCours = () => {
    let minuterie = 0;
    const fin = () => {
      clearTimeout(minuterie);
      document.querySelectorAll('.product-details .add-to-cart-button.da-ajout-en-cours').forEach((b) => {
        b.classList.remove('da-ajout-en-cours');
        b.removeAttribute('aria-busy');
        b.querySelectorAll('.da-chargement').forEach((c) => c.remove());
      });
    };
    document.addEventListener('click', (e) => {
      const b = e.target.closest?.('.product-details .add-to-cart-button');
      if (!b || b.disabled || b.classList.contains('da-ajout-en-cours') || b.closest('.da-entretien--ajout')) return;
      if (document.body.classList.contains('da-pointure-a-choisir')) return;
      b.appendChild(chargement());
      b.classList.add('da-ajout-en-cours');
      b.setAttribute('aria-busy', 'true');
      // Horizon remplace la fenêtre du panier en la remplissant : on regarde toutes les 80 ms si un tiroir est ouvert
      // (un observateur posé sur l'ancienne fenêtre ne voyait jamais l'ouverture).
      const depart = performance.now();
      const verifier = () => {
        if (document.querySelector('.theme-drawer__dialog[open]') || performance.now() - depart > 6000) return fin();
        minuterie = setTimeout(verifier, 80);
      };
      clearTimeout(minuterie);
      minuterie = setTimeout(verifier, 80);
    }, true);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ajoutEnCours);
  else ajoutEnCours();

  /* ---------- Accessoires sous le bouton d'achat : ajout sans ouvrir le panier (13/10) ----------
     Son retour iPhone de 2 h 47 : « quand j'ajoute un produit upsell ça m'ouvre le panier […] c'est uniquement quand il
     ajoute la paire de chaussure qu'il faudrait que ça fasse ça ». Le tiroir d'Horizon (cart-drawer.js) s'ouvre à chaque
     ajout quand cart-drawer-component porte auto-open, et il lit l'attribut au moment de l'événement : on le lui retire
     le temps de l'événement d'un accessoire (écoute en capture sur window, donc avant lui) et on le remet aussitôt après.
     Le compteur du panier, en haut, se met à jour comme avant. Le bouton montre où on en est : rond qui tourne, puis
     « Ajouté » coché ; un 2e appui ne rajoute rien ; changer de couleur le remet à « Ajouter ». */
  const accessoiresSansTiroir = () => {
    const DANS = '.da-entretien--ajout';
    const coche = () => {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 -960 960 960');
      svg.setAttribute('aria-hidden', 'true');
      const trace = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      trace.setAttribute('d', 'm382-339.38 345.54-345.54q8.92-8.93 20.88-9.12 11.96-.19 21.27 9.12 9.31 9.31 9.31 21.38 0 12.08-9.31 21.39l-362.38 363q-10.85 10.84-25.31 10.84-14.46 0-25.31-10.84l-167-167q-8.92-8.93-8.8-21.2.11-12.26 9.42-21.57t21.38-9.31q12.08 0 21.39 9.31L382-339.38Z');
      trace.setAttribute('fill', 'currentColor');
      svg.append(trace);
      return svg;
    };
    // Horizon pose le texte « Ajouter » à nu dans le bouton : on l'entoure une fois pour pouvoir le changer.
    const libelle = (b) => {
      let s = b.querySelector('.da-accessoire__libelle');
      if (!s) {
        const t = [...b.childNodes].find((n) => n.nodeType === 3 && n.textContent.trim());
        s = el('span', { class: 'da-accessoire__libelle', texte: t ? t.textContent.trim() : 'Ajouter' });
        if (t) t.replaceWith(s);
        else b.prepend(s);
        b.dataset.daTexte = s.textContent;
      }
      return s;
    };
    const etat = (b, quoi) => {
      const s = libelle(b);
      clearTimeout(b.daMinuterie);
      b.classList.toggle('da-accessoire__bouton--encours', quoi === 'encours');
      b.classList.toggle('da-accessoire__bouton--ajoute', quoi === 'ajoute');
      if (quoi === 'ajoute') s.replaceChildren(coche(), document.createTextNode('Ajouté'));
      else s.textContent = b.dataset.daTexte;
      if (quoi) b.setAttribute('aria-disabled', 'true');
      else b.removeAttribute('aria-disabled');
      // Sans réponse du panier au bout de 8 s, le bouton redevient « Ajouter ».
      if (quoi === 'encours') b.daMinuterie = setTimeout(() => etat(b, ''), 8000);
    };
    document.addEventListener('click', (e) => {
      const b = e.target.closest?.(`${DANS} .da-accessoire__bouton`);
      if (!b || b.disabled) return;
      if (b.getAttribute('aria-disabled') === 'true') {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      etat(b, 'encours');
    }, true);
    window.addEventListener('shopify:cart:lines-update', (e) => {
      const formulaire = e.target instanceof Element ? e.target.closest(`${DANS} product-form-component`) : null;
      if (!formulaire) return;
      document.querySelectorAll('cart-drawer-component[auto-open]').forEach((t) => {
        t.removeAttribute('auto-open');
        setTimeout(() => t.setAttribute('auto-open', ''), 0);
      });
      const b = formulaire.querySelector('.da-accessoire__bouton');
      if (!b) return;
      etat(b, 'encours');
      e.promise?.then((r) => etat(b, r?.detail?.didError ? '' : 'ajoute'), () => etat(b, ''));
    }, true);
    document.addEventListener('change', (e) => {
      if (!e.target.matches?.(`${DANS} select[name="id"]`)) return;
      const b = e.target.closest('.da-accessoire')?.querySelector('.da-accessoire__bouton');
      if (b) etat(b, '');
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', accessoiresSansTiroir);
  else accessoiresSansTiroir();

  /* ---------- Le panier s'ouvre en bas (11/10) ----------
     Sur téléphone, la liste du tiroir ne fait que quelques centimètres : « Complétez l'ensemble » tombait sous le bas
     visible et seul le bord de son cadre dépassait (« quand on ouvre le panier, on voit le bas comme ça y'a l'upsell »).
     À l'ouverture du tiroir, la liste défile jusqu'en bas : la dernière paire ajoutée (en bas de la liste) et les
     accessoires sont visibles, on remonte pour le reste. Pas de défilement quand le tiroir se met à jour déjà ouvert. */
  const panierEnBas = () => {
    let ouvert = false;
    let prevu = false;
    // La liste se remplit parfois après l'ouverture (téléphone lent) : on réessaie toutes les 100 ms pendant 1,5 s,
    // et on s'arrête dès que le client touche lui-même la liste.
    let essais = 0;
    let touche = false;
    const descendre = () => {
      const d = [...document.querySelectorAll('.theme-drawer__dialog[open]')].find((x) => x.querySelector('.cart-drawer__items'));
      const liste = d && d.querySelector('.cart-drawer__items');
      if (!liste || touche) return;
      if (liste.scrollHeight > liste.clientHeight + 4) liste.scrollTop = liste.scrollHeight;
      if (++essais < 15) setTimeout(descendre, 100);
    };
    ['wheel', 'touchstart', 'keydown'].forEach((type) => document.addEventListener(type, (e) => {
      if (e.target.closest?.('.cart-drawer__items')) touche = true;
    }, { passive: true, capture: true }));
    const verifier = () => {
      prevu = false;
      const d = [...document.querySelectorAll('.theme-drawer__dialog[open]')].find((x) => x.querySelector('.cart-drawer__items'));
      if (d && !ouvert) {
        essais = 0;
        touche = false;
        requestAnimationFrame(descendre);
      }
      ouvert = !!d;
    };
    new MutationObserver(() => {
      if (prevu) return;
      prevu = true;
      requestAnimationFrame(verifier);
    }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'] });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', panierEnBas);
  else panierEnBas();

  /* ---------- On voit ce qu'on ouvre (10/10) ----------
     « Voir le tableau des pointures de ce modèle » s'ouvrait sous le bas visible de la fenêtre « Trouver ma pointure » :
     on fait défiler la fenêtre jusqu'au tableau. Même chose pour un volet de la fiche (Description, Guide des
     pointures…) ouvert dans le bas de l'écran : la page remonte pour montrer son contenu. */
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.open) return;
    const resume = d.querySelector(':scope > summary');
    if (!resume) return;
    if (d.matches('.da-conseiller__tableau')) {
      const corps = d.closest('.da-dialogue__corps');
      if (!corps) return;
      requestAnimationFrame(() => {
        corps.scrollBy({ top: resume.getBoundingClientRect().top - corps.getBoundingClientRect().top - 8, behavior: 'smooth' });
      });
    } else if (d.matches('.da-accordeon') && !d.closest('dialog')) {
      requestAnimationFrame(() => {
        const haut = resume.getBoundingClientRect().top;
        if (haut > window.innerHeight * 0.55) window.scrollBy({ top: haut - 90, behavior: 'smooth' });
      });
    }
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', pointureAChoisir);
  else pointureAChoisir();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', barreCollante);
  else barreCollante();
  /* ---------- Fiche produit : flèche « Remonter en haut » (13/10) ----------
     Demandée par l'utilisateur. Ronde, en bas à droite : elle paraît après un écran et demi de défilement et se pose
     au-dessus de la barre d'achat collante quand celle-ci est affichée (règle CSS « .da-barre-achat--visible ~ .da-haut »,
     d'où l'ajout après la barre). Sur ordinateur, c'est parfois .page-wrapper qui défile, pas la fenêtre. */
  const flecheHaut = () => {
    if (!document.querySelector('.product-details') || document.querySelector('.da-haut')) return;
    const bouton = el('button', { type: 'button', class: 'da-haut', 'aria-label': 'Remonter en haut de la page' });
    bouton.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M5.5 11.5 12 5l6.5 6.5"/></svg>';
    document.body.appendChild(bouton);
    const wrapper = document.querySelector('.page-wrapper');
    const maj = () => {
      const haut = Math.max(window.scrollY, wrapper ? wrapper.scrollTop : 0);
      bouton.classList.toggle('da-haut--visible', haut > window.innerHeight * 1.5);
    };
    let attente = 0;
    const surDefilement = () => {
      cancelAnimationFrame(attente);
      attente = requestAnimationFrame(maj);
    };
    window.addEventListener('scroll', surDefilement, { passive: true });
    if (wrapper) wrapper.addEventListener('scroll', surDefilement, { passive: true });
    bouton.addEventListener('click', () => {
      const comment = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      window.scrollTo({ top: 0, behavior: comment });
      if (wrapper && wrapper.scrollTop > 0) wrapper.scrollTo({ top: 0, behavior: comment });
    });
    maj();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', flecheHaut);
  else flecheHaut();

  /* ---------- En-tête : un lien qui ouvre un méga-menu l'ouvre au clic (comme la démo) ---------- */
  document.addEventListener('click', (e) => {
    const lien = e.target.closest('#header-component .menu-list__trigger > a.menu-list__link');
    if (!lien || e.metaKey || e.ctrlKey) return;
    const bouton = lien.parentElement.querySelector('.menu-list__disclosure');
    if (!bouton) return;
    e.preventDefault();
    bouton.click();
  });
  /* ---------- Collection : bouton « Filtrer et trier » flottant une fois la barre du haut dépassée ----------
     … et seulement tant que la grille passe derrière lui (09/10) : plus bas, il cachait les liens
     CGV et mentions légales du pied de page. La bande observée = le bas de l'écran, où il se trouve. */
  (() => {
    const barre = document.querySelector('.da-collection__barre-filtres');
    const flottant = document.querySelector('.da-collection__ouvrir--flottant');
    const liste = document.querySelector('.da-collection__liste');
    if (!barre || !flottant || !('IntersectionObserver' in window)) return;
    let barreDepassee = false, surLaGrille = !liste;
    const maj = () => {
      const visible = barreDepassee && surLaGrille;
      flottant.classList.toggle('da-visible', visible);
      flottant.setAttribute('aria-hidden', visible ? 'false' : 'true');
      flottant.tabIndex = visible ? 0 : -1;
    };
    new IntersectionObserver(([e]) => {
      barreDepassee = !e.isIntersecting && e.boundingClientRect.top < 0;
      maj();
    }).observe(barre);
    if (liste) new IntersectionObserver(([e]) => { surLaGrille = e.isIntersecting; maj(); }, { rootMargin: '-85% 0px 0px 0px' }).observe(liste);
  })();

  /* ---------- Carrousels : barre de défilement (position et part visible) ----------
     Chaque barre est branchée une fois, puis mesurée au défilement et par un ResizeObserver
     (après la mise en page). Avant le 25/09, TOUTES les barres étaient remesurées à chaque
     modification du DOM, n'importe où dans la page (le widget d'avis en fait des centaines) :
     recalculs de mise en page en rafale au chargement. L'observateur ne sert plus qu'à brancher
     les barres qui arrivent après coup (tiroir du panier re-rendu). */
  const brancherBarre = (barre) => {
    const piste = barre.parentElement && barre.parentElement.querySelector('[data-piste]');
    const curseur = barre.firstElementChild;
    if (!piste || !curseur) return;
    barre.dataset.daBarre = '1';
    let prevu = false;
    const maj = () => {
      prevu = false;
      const total = piste.scrollWidth, vu = piste.clientWidth, gauche = piste.scrollLeft;
      barre.hidden = total <= vu + 2;
      const part = Math.max(vu / total, 0.12);
      const pos = total > vu ? gauche / (total - vu) : 0;
      curseur.style.width = (part * 100) + '%';
      curseur.style.transform = 'translateX(' + (pos * (1 / part - 1) * 100) + '%)';
    };
    const planifier = () => {
      if (prevu) return;
      prevu = true;
      requestAnimationFrame(maj);
    };
    piste.addEventListener('scroll', planifier, { passive: true });
    new ResizeObserver(planifier).observe(piste);
  };
  const brancherBarres = () => document.querySelectorAll('.da-defilement:not([data-da-barre])').forEach(brancherBarre);
  brancherBarres();
  let barresEnAttente = false;
  new MutationObserver(() => {
    if (barresEnAttente) return;
    barresEnAttente = true;
    requestAnimationFrame(() => { barresEnAttente = false; brancherBarres(); });
  }).observe(document.body, { childList: true, subtree: true });

})();

/* Fiche produit : un lien vers un volet (#guide-pointures) l'ouvre avant de défiler */
document.addEventListener('click', (e) => {
  const lien = e.target.closest('a[href^="#"]');
  if (!lien) return;
  const ancre = lien.getAttribute('href').slice(1);
  if (!ancre) return;
  const fenetre = document.querySelector(`[data-ancre="${ancre}"]`);
  if (fenetre) {
    e.preventDefault();
    fenetre.click();
    return;
  }
  const cible = document.getElementById(ancre);
  if (cible && cible.tagName === 'DETAILS') cible.open = true;
});

/* Fiche produit : « Lire la suite » de la description (13/10) ; écoute globale, le bloc peut être redessiné */
document.addEventListener('click', (e) => {
  const bouton = e.target.closest('.da-description-courte__suite');
  if (!bouton) return;
  const texte = document.getElementById(bouton.getAttribute('aria-controls'));
  if (!texte) return;
  const ouvert = texte.classList.toggle('est-ouvert');
  bouton.setAttribute('aria-expanded', String(ouvert));
  bouton.textContent = ouvert ? 'Réduire' : 'Lire la suite';
});

/* Fiche produit : prix de la variante dans le bouton d'ajout (« Ajouter au panier · 69 € ») */
(() => {
  const CHIFFRES = /[\d.,   ]+/;
  const enCentimes = (chiffres) => Math.round(parseFloat(chiffres.replace(/[   ]/g, '').replace(',', '.')) * 100);
  // Total en centimes réécrit dans le format d'un prix affiché (« 179,90 € », décimales nulles masquées)
  const formater = (brut, chiffres, total) => {
    const separateur = chiffres.includes(',') ? ',' : '.';
    const entier = Math.floor(total / 100).toLocaleString('fr-FR').replace(/ /g, ' ');
    const centimes = total % 100;
    const montant = centimes ? entier + separateur + String(centimes).padStart(2, '0') : entier;
    return brut.replace(chiffres.trim(), montant);
  };
  const maj = () => {
    const details = document.querySelector('.product-details');
    if (!details) return;
    const prix = details.querySelector('.da-prix-produit__prix');
    const bouton = details.querySelector('.add-to-cart-button');
    if (!prix || !bouton) return;
    let pastille = bouton.querySelector('.da-prix-bouton');
    // Prix de la paire + produits d'entretien cochés, au format de la boutique
    const brut = prix.textContent.trim();
    const chiffres = brut.match(CHIFFRES);
    let valeur = bouton.disabled ? '' : brut;
    const cochees = [...details.querySelectorAll('.da-entretien .da-case:checked')];
    if (valeur && chiffres && cochees.length) {
      const total = cochees.reduce((somme, c) => somme + Number(c.dataset.prix || 0), enCentimes(chiffres[0]));
      valeur = formater(brut, chiffres[0], total);
    }
    // Offre de lancement −20 % (12/10, classe da-promo de <html>) : « ~~179,90 €~~ 143,92 € » dans le bouton ;
    // les produits d'entretien cochés restent au prix normal (hors promo depuis le 13/10)
    let valeurPromo = '';
    // 14/10 : ou promo par le prix Shopify (prix avant réduction barré, ligne --barre), sans la classe da-promo
    const promo = (document.documentElement.classList.contains('da-promo') || details.querySelector('.da-prix-produit__ligne--barre')) && details.querySelector('.da-prix-produit__promo');
    const brutPromo = promo ? promo.textContent.trim() : '';
    const chiffresPromo = brutPromo.match(CHIFFRES);
    if (valeur && chiffresPromo) {
      const extra = cochees.reduce((somme, c) => somme + Number(c.dataset.prix || 0), 0);
      valeurPromo = extra ? formater(brutPromo, chiffresPromo[0], enCentimes(chiffresPromo[0]) + extra) : brutPromo;
    }
    if (!pastille) {
      pastille = document.createElement('span');
      pastille.className = 'da-prix-bouton';
      const ajoute = bouton.querySelector('.add-to-cart__added');
      if (ajoute) ajoute.before(pastille); else bouton.append(pastille);
    }
    if (pastille.textContent === valeur + valeurPromo) return;
    if (valeurPromo) {
      const ancien = document.createElement('s');
      ancien.className = 'da-prix-bouton__ancien';
      ancien.textContent = valeur;
      const nouveau = document.createElement('span');
      nouveau.className = 'da-prix-bouton__nouveau';
      nouveau.textContent = valeurPromo;
      pastille.replaceChildren(ancien, nouveau);
    } else {
      pastille.textContent = valeur;
    }
  };
  // Le prix et le bouton vivent dans la section de la fiche : on n'écoute qu'elle (le widget
  // d'avis, plus bas, modifie la page des centaines de fois au chargement).
  // Au changement de pointure, le thème redessine le bouton sans notre prix : on le remet tout de
  // suite (l'observateur passe avant l'affichage), sinon le texte saute 60 ms (remarque du 09/10).
  let attente;
  const zonePrix = document.querySelector('.product-details')?.closest('.shopify-section') || document.documentElement;
  new MutationObserver(() => {
    const bouton = document.querySelector('.product-details .add-to-cart-button');
    if (bouton && !bouton.disabled && !bouton.querySelector('.da-prix-bouton')) { maj(); return; }
    clearTimeout(attente); attente = setTimeout(maj, 60);
  }).observe(zonePrix, { subtree: true, childList: true, characterData: true });
  document.addEventListener('DOMContentLoaded', maj);
  document.addEventListener('change', (e) => { if (e.target.closest('.da-entretien')) maj(); });
  maj();
})();

/* Pastilles de couleur des cartes produit (02/10/2026) : toucher une pastille montre la photo de ce
   coloris sur la carte et fait ouvrir la fiche sur lui. La photo de survol est retirée, elle montrait
   souvent un autre coloris. Écoute globale : les grilles rechargées par les filtres en profitent aussi. */
(() => {
  'use strict';
  document.addEventListener('click', (e) => {
    const pastille = e.target.closest?.('.da-pastille');
    if (!pastille) return;
    e.preventDefault();
    const carte = pastille.closest('.da-carte');
    if (!carte) return;
    carte.querySelectorAll('.da-pastille').forEach((p) => p.setAttribute('aria-pressed', p === pastille ? 'true' : 'false'));
    const image = carte.querySelector('.da-carte__image--1');
    if (image && pastille.dataset.src) {
      if (pastille.dataset.srcset) image.srcset = pastille.dataset.srcset;
      image.src = pastille.dataset.src;
      carte.querySelector('.da-carte__survol')?.remove();
    }
    if (pastille.dataset.lien) carte.querySelectorAll('a[href]').forEach((a) => { a.href = pastille.dataset.lien; });
  });
})();

/* Noms de couleur sous les vignettes de la fiche (08/10/2026). Son choix : « une seule ligne, mais si ça devient trop
   petit, deux lignes tout aligné ». Seulement sur les fiches où un nom passe sur deux lignes : on essaie des tailles un
   peu plus petites pour tous les noms de la fiche (11 px au minimum) ; si l'un d'eux ne tient toujours pas dans sa
   case, deux lignes mais photos et noms alignés (styles : css/99zb-noms-couleur-0810.css). */
(() => {
  'use strict';
  const largeurTexte = (e) => {
    const r = document.createRange();
    r.selectNodeContents(e);
    return r.getBoundingClientRect().width;
  };
  const ajuster = () => {
    document.querySelectorAll('.product-details fieldset.variant-option').forEach((f) => {
      if (!f.querySelector('.da-vignette-couleur')) return;
      const textes = [...f.querySelectorAll('.variant-option__button-label__text')];
      if (!textes.length) return;
      f.classList.remove('da-couleurs--une-ligne', 'da-couleurs--alignees');
      f.style.removeProperty('--da-taille-nom');
      // getComputedStyle est vivant : on relève la taille de base AVANT de poser la classe qui la réduit
      const style = getComputedStyle(textes[0]);
      const base = parseFloat(style.fontSize);
      const ligne = parseFloat(style.lineHeight) || base * 1.2;
      if (!textes.some((e) => e.getBoundingClientRect().height > ligne * 1.5)) return;
      const tient = () => textes.every((e) => {
        const carte = e.closest('label');
        const cs = getComputedStyle(carte);
        return largeurTexte(e.querySelector('.da-nom-couleur') || e) <= carte.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) - 2;
      });
      f.classList.add('da-couleurs--une-ligne');
      for (let taille = Math.floor(base) - 1; taille >= 11; taille -= 1) {
        f.style.setProperty('--da-taille-nom', `${taille}px`);
        if (tient()) return;
      }
      f.classList.remove('da-couleurs--une-ligne');
      f.style.removeProperty('--da-taille-nom');
      f.classList.add('da-couleurs--alignees');
    });
  };
  const zone = document.querySelector('.product-details')?.closest('.shopify-section');
  if (!zone) return;
  let attente;
  const planifier = () => { clearTimeout(attente); attente = setTimeout(ajuster, 80); };
  // Horizon redessine le sélecteur à chaque changement de variante : on refait le réglage (seulement les ajouts
  // d'éléments, nos propres classes ne relancent rien)
  new MutationObserver(planifier).observe(zone, { subtree: true, childList: true });
  window.addEventListener('resize', planifier);
  document.fonts?.ready.then(planifier);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ajuster);
  else ajuster();
})();

/* 11/10/2026 — Compteur anonyme (Tokoofast, /api/compteur).
   Shopify ne compte un visiteur, son ajout au panier ou son passage en caisse que s'il accepte les cookies : environ
   la moitié des visiteurs échappait aux chiffres. Ces signaux comptent tout le monde, sans cookie ni identifiant.
   « ?compteur=moi » sur une page du site retire cet appareil du compte (pour ses propres essais) ; « ?compteur=tous »
   l'y remet.
   13/10 (ses oui aux n° 18 et 19) :
   - le serveur répond « exclu » quand le signal part de sa box : l'appareil est alors marqué comme le sien et reste
     hors du compte ensuite, même en 4G. D'où fetch keepalive au lieu de sendBeacon, dont la réponse ne se lit pas ;
   - « visite-ajout » une seule fois par page vue où un ajout au panier réussit ; le passage en caisse une fois par page.
   13/10 au soir (v3, « tracker au max pour comprendre », son choix B du 13/10 : tout pour tout le monde, usage
   interne ; il a été prévenu que la grille de la CNIL de juillet 2025 réserve le canal, les mots cherchés et le
   détail par visite aux visiteurs qui acceptent les cookies) :
   - page vue avec d'où vient la visite (canal et nom du site précédent), fonction utilisée sur la page (libellé
     seulement), pointure choisie, défilement, temps passé, vitesse d'affichage, erreurs JavaScript, recherches et
     mots cherchés ; plus de signal « pub » (le canal « pub » le remplace) ;
   - « k: 1 » quand le visiteur a accepté les statistiques dans la bannière Shopify, et un signal « consentement » par
     page : seulement pour savoir quelle part des visiteurs accepte ;
   - « ?mesure=non » coupe tout envoi depuis cet appareil, « ?mesure=oui » le rétablit (rien dans la politique de
     confidentialité ni aucun lien : son choix du 13/10, usage interne) ;
   - appareil du propriétaire : un signal « moi » par page et rien d'autre, pour que le serveur écarte aussi ses passages
     en caisse (pixel Shopify, même adresse et même navigateur) ;
   - une page préchargée par Chrome (règles de spéculation du thème, au survol ou à l'approche d'un lien) ne compte
     qu'au moment où le visiteur l'ouvre vraiment : avant, elle comptait comme vue même s'il n'y allait jamais ;
   - un retour arrière servi depuis la mémoire du navigateur compte comme une nouvelle page vue ;
   - repère du visiteur (« v », 13/10, « pas de limite ») : un nombre tiré au hasard, gardé sur l'appareil
     (localStorage « da-v ») et renouvelé au bout de 395 jours, pour savoir s'il revient et combien de visites
     précèdent un achat. Safari l'efface après 7 jours sans visite. Rien sur l'appareil du propriétaire ;
   - « retrait » : produit enlevé du panier, ou quantité baissée (n° de variante), pour comprendre les abandons ;
   - « liste » : vignettes produit (.da-carte) vues à l'écran et vignette cliquée, sur toute page (collections,
     accueil, suggestions des fiches), envoyées au départ de la page par paquets de 15 : taux de clic par produit ;
   - panier ouvert depuis l'icône, panier fermé sans commander (actions « panier-ouvert », « panier-ferme ») ;
   - « rage » : 3 clics ou plus en moins d'une seconde au même endroit (bouton qui ne répond pas, photo qu'on
     croit cliquable…), avec une courte description de l'élément ; 3 au plus par page. */
(() => {
  'use strict';
  const ADRESSE = 'https://tokoofast.chaussette-en-laine.com/api/compteur';
  const params = new URLSearchParams(location.search);
  const lire = (cle) => {
    try { return localStorage.getItem(cle); } catch (e) { return null; }
  };
  const ecrire = (cle, valeur) => {
    try {
      if (valeur === null) localStorage.removeItem(cle);
      else localStorage.setItem(cle, valeur);
    } catch (e) { /* stockage indisponible */ }
  };

  // Opposition : confirmation visible, puis plus aucun envoi
  const avisMesure = (coupee) => {
    const montrer = () => {
      const avis = document.createElement('p');
      avis.setAttribute('role', 'status');
      avis.style.cssText = 'position:fixed;left:50%;bottom:calc(16px + env(safe-area-inset-bottom));transform:translateX(-50%);z-index:2147483000;margin:0;width:max-content;max-width:calc(100% - 32px);padding:12px 18px;border-radius:999px;background:#111;color:#fff;font:500 14px/1.4 system-ui,sans-serif;text-align:center;box-shadow:0 6px 24px rgba(0,0,0,.25)';
      avis.textContent = coupee
        ? 'Mesure d’audience désactivée sur cet appareil.'
        : 'Mesure d’audience réactivée sur cet appareil.';
      document.body.appendChild(avis);
      setTimeout(() => avis.remove(), 8000);
    };
    if (document.body) montrer();
    else document.addEventListener('DOMContentLoaded', montrer);
  };
  const choix = params.get('mesure');
  if (choix === 'non') ecrire('da-mesure-non', '1');
  if (choix === 'oui') ecrire('da-mesure-non', null);
  if (choix === 'non' || choix === 'oui') avisMesure(choix === 'non');
  if (lire('da-mesure-non') === '1') return;

  if (params.get('compteur') === 'moi') ecrire('da-compteur-moi', '1');
  if (params.get('compteur') === 'tous') ecrire('da-compteur-moi', null);
  // Ni l'éditeur de thème, ni un thème en aperçu, ni un navigateur piloté par un programme
  const S = window.Shopify || {};
  if (navigator.webdriver || S.designMode || (S.theme && S.theme.role && S.theme.role !== 'main')) return;
  const fetchNatif = window.fetch ? window.fetch.bind(window) : null;
  if (!fetchNatif && !navigator.sendBeacon) return;
  const moi = () => lire('da-compteur-moi') === '1';
  let visiteur = '';

  // Accord « statistiques » de la bannière Shopify. « accord » garde le dernier choix annoncé pendant cette page
  // (l'événement peut arriver avant que l'API ne soit à jour).
  let accord = null;
  const consenti = () => {
    if (accord !== null) return accord;
    try {
      const cp = window.Shopify && window.Shopify.customerPrivacy;
      return !!(cp && cp.analyticsProcessingAllowed());
    } catch (e) { return false; }
  };

  // Corps en texte : requête « simple », sans pré-vérification CORS. Seul « exclu: true » pose le drapeau ;
  // « exclu: false » veut dire que le réseau n'est pas le sien, pas que l'appareil n'est pas le sien.
  const envoyer = (e, o, plus, balise) => {
    if (e !== 'moi' && moi()) return;
    const corps = Object.assign({ e, o: o === undefined ? '' : o }, plus || {});
    if (consenti()) corps.k = 1;
    if (visiteur && e !== 'moi') corps.v = visiteur;
    const texte = JSON.stringify(corps);
    if (texte.length > 1000) return;
    if (balise || !fetchNatif) {
      try { navigator.sendBeacon(ADRESSE, new Blob([texte], { type: 'text/plain' })); } catch (err) { /* rien */ }
      return;
    }
    try {
      fetchNatif(ADRESSE, { method: 'POST', body: texte, keepalive: true, credentials: 'omit' })
        .then((r) => (r.status === 200 ? r.json() : null))
        .then((j) => {
          if (j && j.exclu === true) ecrire('da-compteur-moi', '1');
        })
        .catch(() => {});
    } catch (err) { /* rien */ }
  };

  const demarrer = () => {
    // Appareil du propriétaire : un seul signal, pour que le serveur reconnaisse aussi sa visite à la caisse
    if (moi()) {
      envoyer('moi', '');
      return;
    }
    visiteur = (() => {
      try {
        const garde = JSON.parse(localStorage.getItem('da-v') || 'null');
        if (garde && /^[0-9a-f]{16}$/.test(garde.id) && Date.now() - garde.t < 395 * 864e5) return garde.id;
        const octets = new Uint8Array(8);
        crypto.getRandomValues(octets);
        const id = Array.from(octets, (o) => o.toString(16).padStart(2, '0')).join('');
        localStorage.setItem('da-v', JSON.stringify({ id, t: Date.now() }));
        return id;
      } catch (e) {
        return '';
      }
    })();

    const fiche = location.pathname.match(/\/products\/([a-z0-9-]+)/);
    const chemin = location.pathname.toLowerCase().slice(0, 120);
    const cle = fiche ? fiche[1] : chemin;
    // D'où vient la visite, envoyé avec la page vue ; le serveur garde le canal du premier signal de la visite.
    // pub = clic payant (gclid, gbraid, wbraid, utm_medium cpc) ; seo = moteur de recherche ; interne = page suivante
    // d'une visite déjà commencée. Seul le nom du site d'origine part (« r »), jamais l'adresse complète.
    const [canal, origine] = (() => {
      let ref = '';
      try { ref = document.referrer ? new URL(document.referrer).hostname.toLowerCase() : ''; } catch (err) { ref = ''; }
      const source = (params.get('utm_source') || '').toLowerCase();
      const support = (params.get('utm_medium') || '').toLowerCase();
      const ici = location.hostname.replace(/^www\./, '');
      if (params.has('gclid') || params.has('gbraid') || params.has('wbraid') || /^(cpc|ppc|paid)/.test(support)) return ['pub', ''];
      if (support === 'email' || /klaviyo|newsletter/.test(source)) return ['email', ''];
      if (/pinterest/.test(source) || /(^|\.)pinterest\./.test(ref) || ref === 'pin.it') return ['pinterest', ''];
      if (ref && (ref === ici || ref.endsWith('.' + ici) || /(^|\.)(myshopify|shopify)\.com$/.test(ref))) return ['interne', ''];
      if (/(^|\.)(google|bing|yahoo|duckduckgo|ecosia|qwant|yandex|startpage|baidu)\./.test(ref) || ref === 'search.brave.com') return ['seo', ''];
      if (/(^|\.)(facebook|instagram|tiktok|snapchat|youtube|linkedin|reddit|whatsapp|twitter|x)\.com$/.test(ref) || ref === 't.co') return ['social', ref.slice(0, 100)];
      if (!ref) return ['direct', ''];
      return ['site', /^[a-z0-9.-]{1,100}$/.test(ref) ? ref : ''];
    })();
    // Une fiche compte comme page vue ; les autres pages (accueil, collections, blog…) partent en « page » avec leur
    // chemin. Un retour arrière servi par le navigateur vient de la boutique : canal « interne ».
    const pageVue = (retour) => {
      const source = retour ? { c: 'interne' } : origine ? { c: canal, r: origine } : { c: canal };
      if (fiche) envoyer('fiche', fiche[1], source);
      else envoyer('page', chemin, source);
    };
    pageVue(false);

    let consentementEnvoye = false;
    const signalerConsentement = () => {
      if (consentementEnvoye || !consenti()) return;
      consentementEnvoye = true;
      envoyer('consentement', cle, origine ? { c: canal, r: origine } : { c: canal });
    };
    document.addEventListener('visitorConsentCollected', (ev) => {
      accord = !!(ev.detail && ev.detail.analyticsAllowed);
      signalerConsentement();
    });
    // La recherche de la page attend que l'API de la bannière soit chargée (3 s au plus), pour porter « k » si le
    // visiteur a accepté
    let accordPret = false;
    const enAttente = [];
    const accordConnu = () => {
      if (accordPret) return;
      accordPret = true;
      enAttente.splice(0).forEach((f) => f());
    };
    const apresAccord = (f) => (accordPret ? f() : enAttente.push(f));
    try {
      if (window.Shopify && window.Shopify.loadFeatures) {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => {
          if (!err) signalerConsentement();
          accordConnu();
        });
        setTimeout(accordConnu, 3000);
      } else accordConnu();
    } catch (e) {
      accordConnu();
    }

    // Ajout au panier : un envoi vers /cart/add qui réussit (formulaire de la fiche, accessoires de la fiche et du
    // tiroir), avec le temps de réponse de Shopify (« t », en millisecondes)
    const variantes = (corps) => {
      try {
        if (corps instanceof FormData) return corps.getAll('id').map(String);
        if (typeof corps === 'string') {
          const j = JSON.parse(corps);
          return (j.items || [j]).map((i) => String(i.id || ''));
        }
      } catch (e) { /* rien */ }
      return [];
    };
    // « visite-ajout » : sur une fiche, son ancre (comme « fiche ») ; ailleurs, le chemin de la page
    let ajoutCompte = false;
    const fetchOrigine = window.fetch;
    window.fetch = function () {
      const debut = performance.now();
      const reponse = fetchOrigine.apply(window, arguments);
      try {
        const ressource = arguments[0];
        const url = typeof ressource === 'string' ? ressource : (ressource && ressource.url) || '';
        if (/\/cart\/change/.test(url)) {
          // Retrait (13/10) : produit enlevé du panier ou quantité baissée. Horizon envoie le n° de ligne ; la ligne
          // du panier affiché donne, avant la réponse, la variante (data-key « 123:… ») et la quantité d'avant.
          let ligne = NaN;
          let quantite = NaN;
          let cleLigne = '';
          try {
            const j = JSON.parse(arguments[1] && arguments[1].body);
            ligne = Number(j.line);
            quantite = Number(j.quantity);
            cleLigne = j.id ? String(j.id) : '';
          } catch (e) { /* corps illisible : rien */ }
          const lignes = [...((document.querySelector('cart-items-component') || document).querySelectorAll('[ref="cartItemRows[]"]'))];
          const rang = cleLigne ? lignes.find((l) => l.dataset.key === cleLigne) : lignes[ligne - 1];
          const variante = ((rang && rang.dataset.key) || cleLigne).split(':')[0];
          const champ = rang && rang.querySelector('input[name="updates[]"]');
          const avant = champ ? Number(champ.dataset.cartQuantity || champ.value) : NaN;
          reponse.then((r) => {
            if (r.ok && /^\d+$/.test(variante) && Number.isFinite(quantite) && Number.isFinite(avant) && quantite < avant) envoyer('retrait', [variante]);
          }, () => {});
        }
        if (/\/cart\/add/.test(url)) {
          const ids = variantes(arguments[1] && arguments[1].body).filter((v) => /^\d+$/.test(v)).slice(0, 5);
          reponse.then((r) => {
            if (!r.ok) return;
            envoyer('panier', ids.length ? ids : '', { t: Math.min(30000, Math.round(performance.now() - debut)) });
            if (!ajoutCompte) {
              ajoutCompte = true;
              envoyer('visite-ajout', cle);
            }
          }, () => {});
        }
      } catch (e) { /* rien */ }
      return reponse;
    };

    // Fonctions utilisées : un libellé, une seule fois par page vue. Jamais ce que le visiteur écrit.
    const faites = new Set();
    const action = (libelle) => {
      if (faites.has(libelle)) return;
      faites.add(libelle);
      envoyer('action', libelle, fiche ? { f: fiche[1] } : null);
    };
    // Du plus précis au plus général : la première règle qui correspond l'emporte
    const REGLES = [
      ['[data-da-conseiller-choisir]', 'conseiller-fin'],
      ['.da-pointure-pastille, [data-da-vers-pointure]', 'conseiller'],
      ['.da-guide-lien, .da-fiche-technique__guide, a[href="#guide-pointures"]', 'guide'],
      ['.da-description-courte__suite', 'lire-suite'],
      ['[data-da-tout]', 'avis-tous'],
      ['[data-da-tout-voir]', 'avis-photos'],
      ['[data-da-savoir]', 'avis-savoir'],
      ['.da-avis3__carte', 'avis'],
      ['.da-note-produit, a[href="#avis"]', 'avis-note'],
      ['.da-offre2', 'offre'],
      ['.da-klarna-bouton', 'klarna'],
      ['.da-barre-achat button, .da-barre-achat a', 'barre'],
      ['.add-to-cart-button', 'ajout-clic'],
      ['button[name="plus"], button[name="minus"]', 'quantite'],
      ['.da-carte a', 'suggestion'],
      ['a[href="/pages/contact"], a[href^="mailto:"], a[href^="tel:"]', 'contact'],
      ['media-gallery .slideshow-control', 'photos'],
      ['a[href="/search"], [aria-controls="search-modal"]', 'recherche-ouverte'],
      ['[aria-controls="cart-drawer"], [data-testid="cart-drawer-trigger"]', 'panier-ouvert'],
    ];
    document.addEventListener('click', (ev) => {
      // Seulement les vrais clics : le lien « Guide des pointures » clique lui-même sur « Trouver ma pointure »
      const cible = ev.isTrusted && ev.target instanceof Element ? ev.target : null;
      if (!cible) return;
      const sommaire = cible.closest('summary');
      if (sommaire) {
        const details = sommaire.parentElement;
        // Seulement à l'ouverture
        if (details && details.tagName === 'DETAILS' && !details.open) {
          if (sommaire.matches('.da-menu__lien, .header__icon--menu, .menu-drawer__menu-item')) action('menu');
          else {
            const texte = sommaire.textContent.trim().toLowerCase();
            if (/^description/.test(texte)) action('description');
            else if (/^caract/.test(texte)) action('caracteristiques');
            else if (/pointure/.test(texte) && /guide|tableau/.test(texte)) action('guide');
            else if (/^entretien/.test(texte)) action('entretien');
            else if (/^livraison/.test(texte)) action('livraison');
            else action('faq');
          }
        }
        return;
      }
      for (const [selecteur, libelle] of REGLES) {
        if (cible.closest(selecteur)) {
          // Les cartes produit d'une collection sont la navigation normale : « suggestion » ne vaut que sur une fiche
          if (libelle !== 'suggestion' || fiche) action(libelle);
          return;
        }
      }
    }, { capture: true, passive: true });

    // Pointure et couleur choisies, filtres et tri des collections
    const pointures = new Set();
    document.addEventListener('change', (ev) => {
      const t = ev.target;
      if (!(t instanceof HTMLInputElement || t instanceof HTMLSelectElement)) return;
      if (t instanceof HTMLInputElement && t.type === 'radio' && t.dataset.optionName) {
        const nom = t.dataset.optionName.toLowerCase();
        if (/pointure|taille/.test(nom)) {
          const v = String(t.value || '').trim().toLowerCase();
          if (!/^[0-9a-z ,./-]{1,12}$/.test(v) || pointures.has(v)) return;
          pointures.add(v);
          const plus = { f: fiche ? fiche[1] : '' };
          if (t.dataset.optionAvailable === 'false') plus.x = 1;
          envoyer('pointure', v, plus);
        } else if (/couleur|coloris/.test(nom)) action('couleur');
        return;
      }
      if (/^filter\./.test(t.name || '')) action('filtre');
      else if (t.name === 'sort_by') action('tri');
    }, { capture: true, passive: true });

    // Photos de la fiche balayées au doigt (la galerie défile aussi seule au choix d'une couleur : on attend un toucher)
    const brancherGalerie = () => {
      const piste = document.querySelector('media-gallery slideshow-slides');
      if (!piste) return;
      let touche = 0;
      let depart = piste.scrollLeft;
      const toucher = () => {
        touche = Date.now();
        depart = piste.scrollLeft;
      };
      piste.addEventListener('pointerdown', toucher, { passive: true });
      piste.addEventListener('touchstart', toucher, { passive: true });
      piste.addEventListener('scroll', () => {
        if (Date.now() - touche < 2000 && Math.abs(piste.scrollLeft - depart) > piste.clientWidth * 0.5) action('photos');
      }, { passive: true });
    };

    // Recherche : faite, sans résultat, et les mots (nettoyés : ni adresse e-mail ni suite de 4 chiffres)
    const terme = (brut) => {
      const v = String(brut || '').toLowerCase().replace(/\s+/g, ' ').trim().slice(0, 40);
      return v && !v.includes('@') && !/\d{4,}/.test(v) ? v : '';
    };
    const recherchePage = () => {
      if (!/^\/search\/?$/.test(location.pathname) || !params.get('q')) return;
      const titre = (document.querySelector('meta[property="og:title"]') || {}).content || document.title || '';
      const nb = /(\d+)\s+r[ée]sultat/.exec(titre);
      const plus = nb && nb[1] === '0' ? { z: 1 } : {};
      const v = terme(params.get('q'));
      if (v) plus.q = v;
      envoyer('recherche', 'page', plus);
    };
    let dernierTerme = '';
    let minuterie = 0;
    document.addEventListener('input', (ev) => {
      const champ = ev.target;
      if (!(champ instanceof HTMLInputElement) || champ.name !== 'q' || !champ.closest('predictive-search-component')) return;
      clearTimeout(minuterie);
      minuterie = setTimeout(() => {
        const v = terme(champ.value);
        if (v.length < 3 || v === dernierTerme) return;
        dernierTerme = v;
        // Résultat ou non : demandé à Shopify (la liste affichée garde des suggestions même sans résultat)
        fetchNatif('/search/suggest.json?q=' + encodeURIComponent(v) + '&resources[type]=product&resources[limit]=1', { credentials: 'omit' })
          .then((r) => (r.ok ? r.json() : null))
          .then((j) => {
            const produits = j && j.resources && j.resources.results && j.resources.results.products;
            const plus = Array.isArray(produits) && produits.length === 0 ? { z: 1 } : {};
            plus.q = v;
            envoyer('recherche', 'rapide', plus);
          })
          .catch(() => {});
      }, 1500);
    }, { capture: true, passive: true });

    // Erreurs JavaScript : message nettoyé (ni adresse e-mail, ni suite de chiffres, ni paramètres d'adresse), fichier
    // et ligne ; 3 au plus par page. Les erreurs de réseau (bloqueur de publicité, coupure) et d'extensions sont ignorées.
    const nettoyer = (texte) => String(texte || '')
      .replace(/[^\s@'"]+@[^\s@'"]+/g, '[e-mail]')
      .replace(/https?:\/\/[^\s'")]+/g, (u) => {
        try { const x = new URL(u); return x.host + x.pathname; } catch (e) { return '[adresse]'; }
      })
      .replace(/\d{4,}/g, '#')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 120);
    const sourceDe = (fichier, ligne) => {
      let s = 'script de la page';
      try {
        if (fichier && fichier.split('#')[0] !== location.href.split('#')[0]) {
          const u = new URL(fichier, location.href);
          const nom = u.pathname.split('/').pop() || u.pathname;
          const chezNous = u.host === location.host || /(^|\.)(shopify|myshopify)\.com$/.test(u.host);
          s = chezNous ? nom : u.host + '/' + nom;
        }
      } catch (e) { /* rien */ }
      return (s + (ligne ? ':' + ligne : '')).slice(0, 80);
    };
    const erreursVues = new Set();
    const signalerErreur = (message, fichier, ligne) => {
      if (erreursVues.size >= 3) return;
      // Réseau coupé ou bloqué (bloqueur de publicité) : rien à corriger sur le site. Testé sur le message entier.
      if (/failed to fetch|load failed|networkerror|network error|network failure|the network connection was lost|aborterror|the operation was aborted|error completing request|schemas:/i.test(String(message || ''))) return;
      const m = nettoyer(message);
      if (!m || /^script error\.?$/i.test(m) || /resizeobserver loop/i.test(m)) return;
      if (/^(chrome|moz|safari|safari-web|ms-browser)-extension:/.test(fichier || '')) return;
      const s = sourceDe(fichier, ligne);
      if (erreursVues.has(m + '|' + s)) return;
      erreursVues.add(m + '|' + s);
      envoyer('erreur', m, { s, f: cle });
    };
    window.addEventListener('error', (ev) => {
      // Une image ou un fichier qui ne charge pas arrive ici aussi, sans être une ErrorEvent : ignoré
      if (ev instanceof ErrorEvent) signalerErreur(ev.message || (ev.error && ev.error.message), ev.filename, ev.lineno);
    }, true);
    window.addEventListener('unhandledrejection', (ev) => {
      const r = ev.reason;
      const pile = r && typeof r.stack === 'string' ? /(https?:\/\/[^\s)]+?):(\d+):\d+/.exec(r.stack) : null;
      signalerErreur(r && (r.message || (typeof r === 'string' ? r : '')), pile ? pile[1] : '', pile ? pile[2] : '');
    });

    // Vignettes produit vues (au moins 60 % à l'écran) et vignette cliquée, sur toute page : un produit vu souvent et
    // rarement cliqué, c'est d'abord sa photo principale qui est en cause
    const ancreDe = (carte) => {
      const lien = carte.querySelector('a[href*="/products/"]');
      const m = lien && /\/products\/([a-z0-9-]+)/.exec(lien.getAttribute('href') || '');
      return m ? m[1] : '';
    };
    let vignettesVues = new Set();
    let vignetteCliquee = null;
    let observateurVignettes = null;
    const brancherVignettes = () => {
      if (!('IntersectionObserver' in window)) return;
      observateurVignettes = new IntersectionObserver((entrees) => {
        for (const en of entrees) {
          if (!en.isIntersecting) continue;
          const a = ancreDe(en.target);
          if (a && vignettesVues.size < 90) vignettesVues.add(a);
          observateurVignettes.unobserve(en.target);
        }
      }, { threshold: 0.6 });
      document.querySelectorAll('.da-carte').forEach((c) => observateurVignettes.observe(c));
    };
    document.addEventListener('click', (ev) => {
      if (!ev.isTrusted || vignetteCliquee || !(ev.target instanceof Element)) return;
      const carte = ev.target.closest('.da-carte');
      if (!carte) return;
      const a = ancreDe(carte);
      if (!a) return;
      const rang = [...document.querySelectorAll('.da-carte')].indexOf(carte) + 1;
      vignetteCliquee = { a, rang };
      vignettesVues.add(a);
    }, { capture: true, passive: true });
    const envoyerVignettes = () => {
      const vues = [...vignettesVues];
      if (!vues.length && !vignetteCliquee) return;
      for (let i = 0; i < Math.max(vues.length, 1); i += 15) {
        const plus = { h: vues.slice(i, i + 15) };
        if (i === 0 && vignetteCliquee) {
          plus.x = vignetteCliquee.a;
          plus.xp = vignetteCliquee.rang;
        }
        envoyer('liste', cle, plus, true);
      }
      vignettesVues = new Set();
      vignetteCliquee = null;
    };

    // Défilement, temps passé (onglet visible seulement) et vitesse d'affichage (LCP) : un signal « fin » par page
    // vue, envoyé quand la page est quittée ou cachée
    let defil = 0;
    const mesurerDefil = () => {
      const h = document.documentElement.scrollHeight;
      const part = h > 0 ? (window.scrollY + window.innerHeight) / h : 1;
      if (part > defil) defil = Math.min(1, part);
    };
    let attenteDefil = 0;
    window.addEventListener('scroll', () => {
      if (attenteDefil) return;
      attenteDefil = setTimeout(() => {
        attenteDefil = 0;
        mesurerDefil();
      }, 300);
    }, { passive: true });
    // Jusqu'où la fiche a été vue : bloc d'achat, description, avis, suggestions, bas de page
    const JALONS = ['haut', 'description', 'avis', 'suggestions', 'pied'];
    let jalon = 0;
    const brancherJalons = () => {
      if (!fiche || !('IntersectionObserver' in window)) return;
      const rangs = new Map();
      const io = new IntersectionObserver((entrees) => {
        for (const en of entrees) {
          if (!en.isIntersecting) continue;
          jalon = Math.max(jalon, rangs.get(en.target) || 0);
          io.unobserve(en.target);
        }
      });
      [
        [1, '.da-description-courte, details.da-accordeon'],
        [2, '#avis, #avis-modeles'],
        [3, '.da-produits'],
        [4, 'footer'],
      ].forEach(([rang, selecteur]) => {
        const el = document.querySelector(selecteur);
        if (el) {
          rangs.set(el, rang);
          io.observe(el);
        }
      });
    };
    // LCP mesuré depuis l'ouverture réelle de la page (une page préchargée a commencé avant)
    const navigation = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
    const activation = (navigation && navigation.activationStart) || 0;
    let lcp = 0;
    try {
      const po = new PerformanceObserver((liste) => {
        const e = liste.getEntries();
        if (e.length) lcp = e[e.length - 1].startTime;
      });
      po.observe({ type: 'largest-contentful-paint', buffered: true });
      // Le LCP s'arrête à la première action du visiteur
      const arreter = () => {
        const e = po.takeRecords();
        if (e.length) lcp = e[e.length - 1].startTime;
        po.disconnect();
      };
      ['keydown', 'pointerdown'].forEach((type) => window.addEventListener(type, arreter, { once: true, capture: true, passive: true }));
    } catch (e) { /* navigateur sans LCP : pas de « l » */ }
    let visibleDepuis = document.visibilityState === 'visible' ? (activation ? performance.now() : 0) : null;
    let cumul = 0;
    let finEnvoyee = false;
    const envoyerFin = () => {
      if (visibleDepuis !== null) {
        cumul += performance.now() - visibleDepuis;
        visibleDepuis = null;
      }
      if (finEnvoyee) return;
      finEnvoyee = true;
      mesurerDefil();
      const d = defil >= 0.95 ? 100 : defil >= 0.75 ? 75 : defil >= 0.5 ? 50 : defil >= 0.25 ? 25 : 0;
      const plus = { d, t: Math.min(1800, Math.round(cumul / 1000)) };
      if (fiche) plus.j = JALONS[jalon];
      if (lcp > 0) plus.l = Math.min(30000, Math.round(Math.max(0, lcp - activation) / 100) * 100);
      envoyer('fin', cle, plus, true);
      envoyerVignettes();
    };
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') envoyerFin();
      else if (visibleDepuis === null) visibleDepuis = performance.now();
    });
    window.addEventListener('pagehide', envoyerFin);

    // Passage en caisse : bouton « Commander » (tiroir, page panier) ou paiement express
    // sendBeacon ici : la page s'en va aussitôt, et la réponse n'a rien à nous apprendre
    let caisseComptee = false;
    document.addEventListener('click', (ev) => {
      const cible = ev.target instanceof Element && ev.target.closest('[name="checkout"], a[href*="/checkout"], shopify-accelerated-checkout, shopify-accelerated-checkout-cart');
      if (cible && !caisseComptee) {
        caisseComptee = true;
        envoyer('caisse', '', null, true);
      }
    }, { capture: true, passive: true });

    // Retour arrière servi depuis la mémoire du navigateur : la page n'est pas rechargée, c'est pourtant une page vue
    window.addEventListener('pageshow', (ev) => {
      if (!ev.persisted) return;
      pageVue(true);
      consentementEnvoye = false;
      signalerConsentement();
      faites.clear();
      pointures.clear();
      ajoutCompte = false;
      caisseComptee = false;
      defil = 0;
      cumul = 0;
      lcp = 0;
      finEnvoyee = false;
      visibleDepuis = performance.now();
      mesurerDefil();
      vignettesVues = new Set();
      vignetteCliquee = null;
      if (observateurVignettes) document.querySelectorAll('.da-carte').forEach((c) => observateurVignettes.observe(c));
    });

    // Panier fermé sans passer à la caisse (croix, fond ou touche Échap) : la fenêtre du tiroir se ferme
    const brancherTiroir = () => {
      const fenetre = document.querySelector('#cart-drawer dialog');
      if (fenetre) fenetre.addEventListener('close', () => { if (!caisseComptee) action('panier-ferme'); });
    };

    // Clics rageurs : 3 clics ou plus en moins d'une seconde dans un rayon de 30 px
    let rafale = [];
    const ragesVues = new Set();
    const decrire = (el) => {
      const cible = el.closest('button, a, summary, label, input, select, img, [role="button"], [class]') || el;
      const classe = [...cible.classList].find((c) => /^(da-|button|cart|product|slideshow|header|variant)/.test(c)) || cible.classList[0] || '';
      const texte = (cible.getAttribute('aria-label') || cible.textContent || cible.getAttribute('alt') || '').replace(/\s+/g, ' ').trim().slice(0, 30).trim();
      return (cible.tagName.toLowerCase() + (classe ? '.' + classe : '') + (texte ? ' « ' + texte + ' »' : ''))
        .replace(/[^\p{L}\p{N} ._#«»'’:-]/gu, '').slice(0, 80);
    };
    document.addEventListener('click', (ev) => {
      if (!ev.isTrusted || !(ev.target instanceof Element) || ragesVues.size >= 3) return;
      const maintenant = performance.now();
      rafale = rafale.filter((c) => maintenant - c.t < 1000);
      rafale.push({ t: maintenant, x: ev.clientX, y: ev.clientY });
      if (rafale.filter((c) => Math.hypot(c.x - ev.clientX, c.y - ev.clientY) < 30).length < 3) return;
      rafale = [];
      const d = decrire(ev.target);
      if (!d || ragesVues.has(d)) return;
      ragesVues.add(d);
      envoyer('rage', d, fiche ? { f: fiche[1] } : null);
    }, { capture: true, passive: true });

    const auChargement = () => {
      brancherGalerie();
      brancherJalons();
      brancherVignettes();
      brancherTiroir();
      apresAccord(recherchePage);
      mesurerDefil();
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', auChargement);
    else auChargement();
  };

  // Page préchargée par Chrome : rien ne part tant que le visiteur ne l'a pas vraiment ouverte
  if (document.prerendering) document.addEventListener('prerenderingchange', demarrer, { once: true });
  else demarrer();
})();

/* Visionneuse des photos de la fiche (25/10/2026, son choix « 1 »). Le zoom d'Horizon (réglage « zoom » de la galerie)
   s'ouvrait déjà grossi et coupé : il reste éteint. Un appui sur une photo l'ouvre en grand, entière, sur le fond des
   photos (#FAFAFA) ; on glisse pour changer de photo, on pince ou on double-touche pour zoomer ; sur ordinateur, un clic
   zoome là où on clique et la souris déplace la photo. Le bouton retour du téléphone la ferme. Loupe sur la 1re photo.
   Styles : css/99zv-visionneuse-2510.css. */
(() => {
  'use strict';
  const PHOTOS = 'media-gallery .product-media-container--image';
  const ZOOM_DOUBLE = 2.5;
  const ZOOM_MAX = 4;
  const ICONE_FERMER = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  const fleche = (d) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="${d}"/></svg>`;

  const visible = (el) => el.getBoundingClientRect().width > 0;
  const grande = (img, largeur) => {
    try {
      const u = new URL(img.currentSrc || img.src, location.href);
      u.searchParams.set('width', String(largeur));
      return u.href;
    } catch (e) {
      return img.currentSrc || img.src;
    }
  };
  const borne = (v, min, max) => Math.min(max, Math.max(min, v));
  const tactile = () => matchMedia('(hover: none)').matches;

  // Loupe sur la 1re photo affichée (grille sur ordinateur, diaporama sur téléphone)
  const poserLoupe = () => {
    const premiere = [...document.querySelectorAll(PHOTOS)].find(visible);
    document.querySelectorAll('media-gallery .da-loupe').forEach((el) => { if (el !== premiere) el.classList.remove('da-loupe'); });
    if (premiere && !premiere.classList.contains('da-loupe')) premiere.classList.add('da-loupe');
  };

  let boite = null;
  let piste;
  let compteur;
  let vignettes;
  let precedent;
  let suivant;
  let vues = [];
  let index = 0;
  let retourFocus = null;
  let avecHistorique = false;
  // Zoom de la photo affichée
  let echelle = 1;
  let tx = 0;
  let ty = 0;

  const imageCourante = () => vues[index]?.querySelector('img');
  const appliquer = () => {
    const img = imageCourante();
    if (!img) return;
    img.style.transform = echelle > 1 ? `translate(${tx}px, ${ty}px) scale(${echelle})` : '';
    img.classList.toggle('da-visio__img--zoom', echelle > 1);
    piste.classList.toggle('da-visio__piste--fixe', echelle > 1);
  };
  // Le déplacement s'arrête au bord de la photo agrandie
  const limiter = () => {
    const vue = vues[index];
    if (!vue) return;
    const mx = (vue.clientWidth * (echelle - 1)) / 2;
    const my = (vue.clientHeight * (echelle - 1)) / 2;
    tx = borne(tx, -mx, mx);
    ty = borne(ty, -my, my);
  };
  const sansZoom = () => {
    echelle = 1;
    tx = 0;
    ty = 0;
    appliquer();
  };
  // Zoom centré sur un point de l'écran (x, y)
  const zoomerVers = (nouvelle, x, y) => {
    const r = vues[index].getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const ratio = nouvelle / echelle;
    tx = (x - cx) - ((x - cx) - tx) * ratio;
    ty = (y - cy) - ((y - cy) - ty) * ratio;
    echelle = nouvelle;
    if (echelle <= 1.01) return sansZoom();
    limiter();
    appliquer();
  };

  const majCompteur = () => {
    compteur.textContent = `${index + 1} / ${vues.length}`;
    vignettes.querySelectorAll('button').forEach((b, i) => b.setAttribute('aria-current', i === index ? 'true' : 'false'));
    const v = vignettes.children[index];
    if (v) vignettes.scrollTo({ left: v.offsetLeft - (vignettes.clientWidth - v.offsetWidth) / 2, behavior: 'smooth' });
    precedent.disabled = index === 0;
    suivant.disabled = index === vues.length - 1;
  };
  const allerA = (i, doux = true) => {
    i = borne(i, 0, vues.length - 1);
    if (i !== index) sansZoom();
    index = i;
    piste.scrollTo({ left: i * piste.clientWidth, behavior: doux ? 'smooth' : 'instant' });
    majCompteur();
  };

  const construire = () => {
    boite = document.createElement('div');
    boite.className = 'da-visio';
    boite.hidden = true;
    boite.setAttribute('role', 'dialog');
    boite.setAttribute('aria-modal', 'true');
    boite.setAttribute('aria-label', 'Photos du produit');
    boite.innerHTML = `<div class="da-visio__haut"><span class="da-visio__compteur" aria-live="polite"></span>
      <button type="button" class="da-visio__fermer" aria-label="Fermer">${ICONE_FERMER}</button></div>
      <div class="da-visio__corps"><div class="da-visio__piste"></div>
      <button type="button" class="da-visio__fleche da-visio__fleche--prec" aria-label="Photo précédente">${fleche('M15 5l-7 7 7 7')}</button>
      <button type="button" class="da-visio__fleche da-visio__fleche--suiv" aria-label="Photo suivante">${fleche('M9 5l7 7-7 7')}</button></div>
      <p class="da-visio__aide"></p><div class="da-visio__vignettes"></div>`;
    document.body.appendChild(boite);
    piste = boite.querySelector('.da-visio__piste');
    compteur = boite.querySelector('.da-visio__compteur');
    vignettes = boite.querySelector('.da-visio__vignettes');
    precedent = boite.querySelector('.da-visio__fleche--prec');
    suivant = boite.querySelector('.da-visio__fleche--suiv');

    boite.querySelector('.da-visio__fermer').addEventListener('click', () => fermer());
    precedent.addEventListener('click', () => allerA(index - 1));
    suivant.addEventListener('click', () => allerA(index + 1));
    vignettes.addEventListener('click', (e) => {
      const b = e.target.closest('button');
      if (b) allerA([...vignettes.children].indexOf(b));
    });
    // Photo affichée = celle qui occupe la piste après un glissement
    let attente;
    piste.addEventListener('scroll', () => {
      clearTimeout(attente);
      attente = setTimeout(() => {
        const i = borne(Math.round(piste.scrollLeft / piste.clientWidth), 0, vues.length - 1);
        if (i !== index) {
          sansZoom();
          index = i;
          majCompteur();
        }
      }, 80);
    }, { passive: true });
    boite.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') fermer();
      else if (e.key === 'ArrowLeft') allerA(index - 1);
      else if (e.key === 'ArrowRight') allerA(index + 1);
      else if (e.key === 'Tab') {
        // Le focus reste dans la visionneuse
        const f = [...boite.querySelectorAll('button:not([disabled])')].filter(visible);
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    });

    // Souris : un clic zoome là où on clique, la souris déplace la photo, un 2e clic dézoome
    piste.addEventListener('click', (e) => {
      if (tactile() || !(e.target instanceof HTMLImageElement)) return;
      if (echelle > 1) sansZoom();
      else zoomerVers(ZOOM_DOUBLE, e.clientX, e.clientY);
    });
    piste.addEventListener('mousemove', (e) => {
      if (echelle <= 1 || tactile()) return;
      const r = vues[index].getBoundingClientRect();
      const mx = (r.width * (echelle - 1)) / 2;
      const my = (r.height * (echelle - 1)) / 2;
      tx = -((e.clientX - r.left) / r.width - 0.5) * 2 * mx;
      ty = -((e.clientY - r.top) / r.height - 0.5) * 2 * my;
      appliquer();
    });

    // Doigts : pincer (2 doigts), double appui, glisser la photo agrandie (1 doigt)
    let pince = null;
    let glisse = null;
    let dernierAppui = 0;
    const distance = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    piste.addEventListener('touchstart', (e) => {
      if (!(e.target instanceof HTMLImageElement)) return;
      if (e.touches.length === 2) {
        const [a, b] = e.touches;
        pince = { d: distance(a, b), e: echelle };
        glisse = null;
      } else if (e.touches.length === 1) {
        const t = e.touches[0];
        const maintenant = Date.now();
        if (maintenant - dernierAppui < 300) {
          e.preventDefault();
          if (echelle > 1) sansZoom();
          else zoomerVers(ZOOM_DOUBLE, t.clientX, t.clientY);
          dernierAppui = 0;
          return;
        }
        dernierAppui = maintenant;
        if (echelle > 1) glisse = { x: t.clientX, y: t.clientY, tx, ty };
      }
    }, { passive: false });
    piste.addEventListener('touchmove', (e) => {
      if (pince && e.touches.length === 2) {
        e.preventDefault();
        const [a, b] = e.touches;
        zoomerVers(borne((pince.e * distance(a, b)) / pince.d, 1, ZOOM_MAX), (a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2);
      } else if (glisse && e.touches.length === 1) {
        e.preventDefault();
        const t = e.touches[0];
        tx = glisse.tx + (t.clientX - glisse.x);
        ty = glisse.ty + (t.clientY - glisse.y);
        limiter();
        appliquer();
      }
    }, { passive: false });
    piste.addEventListener('touchend', (e) => {
      if (e.touches.length < 2) pince = null;
      if (e.touches.length === 0) glisse = null;
    }, { passive: true });
  };

  const ouvrir = (depart) => {
    if (!boite) construire();
    const photos = [...document.querySelectorAll(PHOTOS)].filter(visible);
    const images = photos.map((p) => p.querySelector('img')).filter(Boolean);
    if (!images.length) return;
    const largeur = Math.min(2400, Math.ceil(Math.max(window.innerWidth, window.innerHeight) * (window.devicePixelRatio || 1) * 1.5));
    piste.innerHTML = images.map((img) => `<div class="da-visio__vue"><img src="${grande(img, largeur)}" alt="${(img.alt || '').replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`)}" draggable="false"></div>`).join('');
    vignettes.innerHTML = images.map((img, i) => `<button type="button" aria-label="Photo ${i + 1}"><img src="${grande(img, 120)}" alt="" loading="lazy"></button>`).join('');
    vues = [...piste.children];
    vignettes.hidden = images.length < 2;
    boite.querySelector('.da-visio__aide').textContent = tactile()
      ? 'Pincez pour zoomer · glissez pour changer de photo'
      : 'Cliquez sur la photo pour zoomer';
    retourFocus = document.activeElement;
    boite.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    index = -1;
    allerA(Math.max(0, photos.indexOf(depart)), false);
    requestAnimationFrame(() => {
      boite.classList.add('da-visio--ouverte');
      boite.querySelector('.da-visio__fermer').focus({ preventScroll: true });
    });
    // Le bouton retour du téléphone ferme la visionneuse au lieu de quitter la fiche
    history.pushState(Object.assign({}, history.state, { daVisio: true }), '');
    avecHistorique = true;
  };

  const fermer = (depuisRetour = false) => {
    if (!boite || boite.hidden) return;
    sansZoom();
    boite.classList.remove('da-visio--ouverte');
    boite.hidden = true;
    document.documentElement.style.overflow = '';
    if (avecHistorique && !depuisRetour) history.back();
    avecHistorique = false;
    retourFocus?.focus?.({ preventScroll: true });
  };
  window.addEventListener('popstate', () => {
    if (!avecHistorique) return;
    avecHistorique = false;
    fermer(true);
  });

  document.addEventListener('click', (e) => {
    const photo = e.target.closest?.(PHOTOS);
    if (!photo || e.target.closest('button, a, video, model-viewer, deferred-media')) return;
    e.preventDefault();
    ouvrir(photo);
  });

  const galerie = document.querySelector('media-gallery');
  if (galerie) {
    const brancher = () => {
      poserLoupe();
      // Le thème redessine la galerie au changement de coloris, et passe de la grille au diaporama selon la largeur
      new MutationObserver(() => requestAnimationFrame(poserLoupe)).observe(galerie.parentElement, { childList: true });
      let delai;
      window.addEventListener('resize', () => { clearTimeout(delai); delai = setTimeout(poserLoupe, 150); });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', brancher);
    else brancher();
  }
})();
